/**
 * Frontend configuration
 * Uses environment variables for different deployment environments
 */

// API Base URL configuration
// In production, this should point to your production API
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Currency configuration
export const CURRENCY_SYMBOL = '€';

// API endpoints
export const API = {
  BASE: API_BASE_URL,
  AUTH: {
    LOGIN: `${API_BASE_URL}/api/v1/auth/login`,
    ME: `${API_BASE_URL}/api/v1/auth/me`,
    REFRESH: `${API_BASE_URL}/api/v1/auth/refresh`,
    LOGOUT: `${API_BASE_URL}/api/v1/auth/logout`,
    LOGOUT_ALL: `${API_BASE_URL}/api/v1/auth/logout-all`,
  },
  PROPERTIES: {
    LIST: `${API_BASE_URL}/api/v1/properties`,
    DETAIL: (id: number) => `${API_BASE_URL}/api/v1/properties/${id}`,
    AVAILABILITY: (id: number) => `${API_BASE_URL}/api/v1/properties/${id}/availability`,
    CALENDAR: (id: number) => `${API_BASE_URL}/api/v1/properties/${id}/calendar`,
    PRICING: (id: number) => `${API_BASE_URL}/api/v1/properties/${id}/pricing`,
  },
  BOOKINGS: {
    CREATE: `${API_BASE_URL}/api/v1/bookings`,
    LIST: `${API_BASE_URL}/api/v1/bookings`,
    DETAIL: (id: number) => `${API_BASE_URL}/api/v1/bookings/${id}`,
    CHECK_AVAILABILITY: `${API_BASE_URL}/api/v1/check-availability`,
  },
  EMBED: {
    CREATE: `${API_BASE_URL}/api/v1/embed`,
    CONFIG: (code: string) => `${API_BASE_URL}/api/v1/embed/${code}/config`,
    PROPERTY_CONFIG: (propertyId: number) => `${API_BASE_URL}/api/v1/properties/${propertyId}/embed-config`,
    APPEARANCE: (configId: number) => `${API_BASE_URL}/api/v1/embed-configs/${configId}/appearance`,
  },
  WAITLIST: {
    LIST: `${API_BASE_URL}/api/v1/waitlist`,
  },
  ADMIN: {
    STATS: `${API_BASE_URL}/api/v1/admin/stats`,
    USERS: `${API_BASE_URL}/api/v1/admin/users`,
    PROPERTIES: `${API_BASE_URL}/api/v1/admin/properties`,
    BOOKINGS: `${API_BASE_URL}/api/v1/admin/bookings`,
  },
  COUPONS: {
    LIST: `${API_BASE_URL}/api/v1/coupons`,
    CREATE: `${API_BASE_URL}/api/v1/coupons`,
    DETAIL: (id: number) => `${API_BASE_URL}/api/v1/coupons/${id}`,
    VALIDATE: `${API_BASE_URL}/api/v1/coupons/validate`,
    BOOKINGS: (id: number) => `${API_BASE_URL}/api/v1/coupons/${id}/bookings`,
  },
  STAY_DISCOUNTS: {
    LIST: (propertyId: number) => `${API_BASE_URL}/api/v1/properties/${propertyId}/stay-discounts`,
    CREATE: (propertyId: number) => `${API_BASE_URL}/api/v1/properties/${propertyId}/stay-discounts`,
    DETAIL: (id: number) => `${API_BASE_URL}/api/v1/stay-discounts/${id}`,
  },
  STRIPE: {
    CONNECT: `${API_BASE_URL}/api/v1/stripe/connect`,
    CONNECT_STATUS: `${API_BASE_URL}/api/v1/stripe/connect/status`,
    CONNECT_REFRESH: `${API_BASE_URL}/api/v1/stripe/connect/refresh`,
    DISCONNECT: `${API_BASE_URL}/api/v1/stripe/disconnect`,
  },
};

// Token storage keys
export const TOKEN_KEYS = {
  ACCESS_TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
  TOKEN_EXPIRY: 'token_expiry',
};

// Request timeout in milliseconds
export const REQUEST_TIMEOUT = 30000; // 30 seconds

// Helper function to create fetch with timeout
export const fetchWithTimeout = async (url: string, options: RequestInit = {}, timeout = REQUEST_TIMEOUT): Promise<Response> => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    throw error;
  }
};

// Helper function to add auth headers
export const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

// Token management utilities
export const tokenUtils = {
  getAccessToken: (): string | null => localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN),
  getRefreshToken: (): string | null => localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN),
  getTokenExpiry: (): number | null => {
    const expiry = localStorage.getItem(TOKEN_KEYS.TOKEN_EXPIRY);
    return expiry ? parseInt(expiry, 10) : null;
  },

  setTokens: (accessToken: string, refreshToken: string, expiresIn: number) => {
    localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
    // Store expiry timestamp (current time + expires_in seconds)
    const expiryTime = Date.now() + expiresIn * 1000;
    localStorage.setItem(TOKEN_KEYS.TOKEN_EXPIRY, expiryTime.toString());
  },

  clearTokens: () => {
    localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.TOKEN_EXPIRY);
  },

  isTokenExpiringSoon: (thresholdMs: number = 60000): boolean => {
    const expiry = tokenUtils.getTokenExpiry();
    if (!expiry) return true;
    return Date.now() + thresholdMs > expiry;
  },
};

// Session expiration handler - will be set by AuthContext
let sessionExpiredHandler: (() => void) | null = null;

export const setSessionExpiredHandler = (handler: () => void) => {
  sessionExpiredHandler = handler;
};

// Refresh token function
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

export const refreshAccessToken = async (): Promise<boolean> => {
  // If already refreshing, wait for that to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = tokenUtils.getRefreshToken();
  if (!refreshToken) {
    return false;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response = await fetchWithTimeout(API.AUTH.REFRESH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        tokenUtils.clearTokens();
        return false;
      }

      const data = await response.json();
      tokenUtils.setTokens(data.access_token, data.refresh_token, data.expires_in);
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      tokenUtils.clearTokens();
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Authenticated fetch wrapper that handles token refresh and session expiration.
 * Use this for all authenticated API calls instead of fetchWithTimeout + getAuthHeaders.
 */
export const authenticatedFetch = async (
  url: string,
  options: RequestInit = {},
  timeout = REQUEST_TIMEOUT
): Promise<Response> => {
  // Check if token is expiring soon and refresh proactively
  if (tokenUtils.isTokenExpiringSoon(60000)) { // 1 minute threshold
    const refreshed = await refreshAccessToken();
    if (!refreshed && sessionExpiredHandler) {
      sessionExpiredHandler();
      throw new Error('Session expired');
    }
  }

  // Make the request with current token
  const response = await fetchWithTimeout(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  }, timeout);

  // Handle 401 - try to refresh token and retry once
  if (response.status === 401) {
    const refreshed = await refreshAccessToken();

    if (refreshed) {
      // Retry the request with new token
      return fetchWithTimeout(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      }, timeout);
    } else {
      // Refresh failed - session expired
      if (sessionExpiredHandler) {
        sessionExpiredHandler();
      }
      throw new Error('Session expired');
    }
  }

  return response;
};
